/**
 * Settings Persistence Utility
 *
 * Saves and loads PDF processing settings (filters, exceptions, preserved pages)
 * to/from localStorage, keyed by filename.
 */

const STORAGE_KEY_PREFIX = 'ecoprint_pdf_';
const STORAGE_VERSION = 1;

/**
 * Generate a storage key from a filename
 * @param {string} filename - PDF filename
 * @returns {string} Storage key
 */
function getStorageKey(filename) {
  return `${STORAGE_KEY_PREFIX}${filename}`;
}

/**
 * Save PDF settings to localStorage
 * @param {string} filename - PDF filename
 * @param {object} settings - Settings object to save
 * @param {object} settings.filters - Global filter settings
 * @param {object} settings.pageExceptions - Page-specific exceptions
 */
export function savePDFSettings(filename, settings) {
  if (!filename) {
    console.warn('Cannot save settings: no filename provided');
    return;
  }

  try {
    const storageKey = getStorageKey(filename);
    const dataToSave = {
      version: STORAGE_VERSION,
      timestamp: new Date().toISOString(),
      filename,
      filters: settings.filters,
      pageExceptions: settings.pageExceptions || {},
    };

    localStorage.setItem(storageKey, JSON.stringify(dataToSave));
    console.log(`Settings saved for: ${filename}`);
  } catch (error) {
    console.error('Failed to save PDF settings:', error);
    // Handle quota exceeded errors gracefully
    if (error.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded. Consider clearing old settings.');
    }
  }
}

/**
 * Load PDF settings from localStorage
 * @param {string} filename - PDF filename
 * @returns {object|null} Saved settings or null if not found
 */
export function loadPDFSettings(filename) {
  if (!filename) {
    console.warn('Cannot load settings: no filename provided');
    return null;
  }

  try {
    const storageKey = getStorageKey(filename);
    const stored = localStorage.getItem(storageKey);

    if (!stored) {
      console.log(`No saved settings found for: ${filename}`);
      return null;
    }

    const data = JSON.parse(stored);

    // Version check (for future compatibility)
    if (data.version !== STORAGE_VERSION) {
      console.warn(`Settings version mismatch for ${filename}. Ignoring.`);
      return null;
    }

    console.log(`Settings loaded for: ${filename} (saved ${data.timestamp})`);

    return {
      filters: data.filters,
      pageExceptions: data.pageExceptions || {},
    };
  } catch (error) {
    console.error('Failed to load PDF settings:', error);
    return null;
  }
}

/**
 * Delete saved settings for a specific PDF
 * @param {string} filename - PDF filename
 */
export function deletePDFSettings(filename) {
  if (!filename) return;

  try {
    const storageKey = getStorageKey(filename);
    localStorage.removeItem(storageKey);
    console.log(`Settings deleted for: ${filename}`);
  } catch (error) {
    console.error('Failed to delete PDF settings:', error);
  }
}

/**
 * List all saved PDF filenames
 * @returns {Array<string>} Array of filenames with saved settings
 */
export function listSavedPDFs() {
  try {
    const savedFiles = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          savedFiles.push({
            filename: data.filename,
            timestamp: data.timestamp,
            key,
          });
        } catch (e) {
          // Skip invalid entries
          console.warn(`Invalid settings entry: ${key}`);
        }
      }
    }

    return savedFiles;
  } catch (error) {
    console.error('Failed to list saved PDFs:', error);
    return [];
  }
}

/**
 * Clear all saved PDF settings
 */
export function clearAllPDFSettings() {
  try {
    const keys = [];

    // Collect all keys first (can't modify during iteration)
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        keys.push(key);
      }
    }

    // Remove all collected keys
    keys.forEach(key => localStorage.removeItem(key));

    console.log(`Cleared ${keys.length} saved PDF settings`);
    return keys.length;
  } catch (error) {
    console.error('Failed to clear PDF settings:', error);
    return 0;
  }
}

/**
 * Get storage usage statistics
 * @returns {object} Storage statistics
 */
export function getStorageStats() {
  try {
    const savedPDFs = listSavedPDFs();
    let totalSize = 0;

    savedPDFs.forEach(pdf => {
      const data = localStorage.getItem(pdf.key);
      if (data) {
        totalSize += data.length;
      }
    });

    return {
      count: savedPDFs.length,
      totalSizeBytes: totalSize,
      totalSizeKB: (totalSize / 1024).toFixed(2),
      pdfs: savedPDFs,
    };
  } catch (error) {
    console.error('Failed to get storage stats:', error);
    return { count: 0, totalSizeBytes: 0, totalSizeKB: '0', pdfs: [] };
  }
}
